import { useMemo } from 'react';
import { ChevronDownIcon } from 'lucide-react';

import { Popover } from '@/components/atoms/popover';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

type Value = string | number;

interface Option<T extends Value> {
  value: T;
  label: string;
  disabled?: boolean;
}

interface MultiSelectProps<T extends Value> {
  label: string;
  selected: T[];
  onChange: (values: T[]) => void;
  options: Option<T>[];
  align?: 'start' | 'center' | 'end';
  className?: string;
}

export function MultiSelect<T extends Value>({
  label,
  selected,
  onChange,
  options,
  align,
  className,
}: MultiSelectProps<T>) {
  const valueLabel = useMemo(() => {
    if (selected.length === 0) return '';
    if (selected.length === 1) {
      const option = options.find((o) => o.value === selected[0]);
      if (option) return option.label;
    }
    return `${selected.length} selected`;
  }, [selected, options]);

  return (
    <Popover
      align={align}
      trigger={
        <Button
          size='sm'
          variant='outline'
          role='combobox'
          className={cn('rounded-md border-[0.5px] border-input shadow-none', className)}
        >
          <div className='flex h-full w-full items-center gap-2 whitespace-nowrap'>
            <span>{label}</span>
            {valueLabel && (
              <>
                <div className='h-1/2 w-px bg-border' />
                <span className='text-muted-foreground'>{valueLabel}</span>
              </>
            )}
          </div>
          <ChevronDownIcon className='ml-auto size-4 text-muted-foreground' />
        </Button>
      }
    >
      <MultiSelectContent selected={selected} onChange={onChange} options={options} />
    </Popover>
  );
}

interface MultiSelectContentProps<T extends Value> {
  selected: T[];
  onChange: (values: T[]) => void;
  options: Option<T>[];
}

export function MultiSelectContent<T extends Value>({ selected, onChange, options }: MultiSelectContentProps<T>) {
  return (
    <Command>
      <CommandInput placeholder='Search ...' />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        <CommandGroup>
          {options.map((option) => (
            <CommandItem
              key={option.value}
              disabled={option.disabled}
              onSelect={() => {
                if (selected.includes(option.value)) {
                  const data = selected.filter((value) => value !== option.value);
                  onChange(data);
                } else {
                  const data = [...selected, option.value];
                  onChange(data);
                }
              }}
            >
              <Checkbox checked={selected.includes(option.value)} />
              {option.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {selected.length > 0 && (
          <div className='sticky bottom-0 z-10 bg-background'>
            <CommandSeparator />
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange([]);
                }}
                className='justify-center text-center'
              >
                Clear selection
              </CommandItem>
            </CommandGroup>
          </div>
        )}
      </CommandList>
    </Command>
  );
}
